/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import debounce from 'react-utils/debounce';
import styled from 'styled-components';
import { GYPSUM, PANTERA } from 'HubStyleTokens/colors';
import { rgba } from 'UIComponents/core/Color';
import { iconFontsLoadedPromise } from 'UIComponents/utils/Fonts';
import UIAbstractProgress from 'UIComponents/progress/UIAbstractProgress';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import { memoize } from '../../utils/functions';
import { identity } from '../../utils/iterables';
import { controlPropType, ToolbarControlTypes } from '../../constants/toolbar';
import ControlGroup from './ControlGroup';
import Disableable from './Disableable';
import Divider from './Divider';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OUTER_TOOLBAR_MARGINS = 16;
const DIVIDER_WIDTH = 17;

// Because flexbox safe centering is not supported in anything other than FF
// https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content
// We need to set justify-content to flex-start and set auto margins on the first and last flex items
const StyledToolbar = styled.div.withConfig({
  displayName: "Toolbar__StyledToolbar",
  componentId: "jubgij-0"
})(["background-color:", ";display:flex;min-height:48px;justify-content:flex-start;overflow:auto;box-shadow:0 1px 5px ", ";user-select:none;"], GYPSUM, rgba(PANTERA, 0.12));
const StyledInnerToolbar = styled.div.withConfig({
  displayName: "Toolbar__StyledInnerToolbar",
  componentId: "jubgij-1"
})(["display:flex;align-items:center;", " ", " ", ""], ({
  multiRow
}) => multiRow && 'flex-wrap: wrap;', ({
  centerControls
}) => centerControls && 'margin: 0 auto;', ({
  expandedHeight
}) => expandedHeight && 'padding-bottom: 48px;');
const StyledRow = styled.div.withConfig({
  displayName: "Toolbar__StyledRow",
  componentId: "jubgij-2"
})(["display:flex;height:48px;align-items:center;"]);
const DividerContainer = styled.div.withConfig({
  displayName: "Toolbar__DividerContainer",
  componentId: "jubgij-3"
})(["display:flex;flex-shrink:0;align-items:center;"]);
const NORMAL_TOOLBAR = null;
let toolbarId = 0;
const getToolbarId = () => ++toolbarId;
const getEmptyMenuControls = iterableToolbar => {
  const emptyMenuControls = [];
  iterableToolbar.forEach((row, rowIdx) => {
    const emptyControls = row.reduce((acc, group) => {
      return [...acc, ...group.controls.filter(control => control.menuItems && control.menuItems.length === 0)];
    }, []);
    emptyMenuControls[rowIdx] = emptyControls;
  });
  return emptyMenuControls;
};
export default class Toolbar extends Component {
  constructor(props) {
    super(props);
    this.getInnerWidths = (initialWidths = []) => {
      // Accumulate the [offsetWidth] of each row's children for better accuracy
      return this.rows.filter(row => !!row).map((row, rowIdx) => Array.from(row.children).reduce((acc, {
        offsetWidth
      }) => acc + offsetWidth, initialWidths[rowIdx] || 0));
    };
    this.getTotalInnerWidth = maxInnerWidths => {
      return maxInnerWidths.reduce((acc, width) => acc + width, 0);
    };
    this.getCollapsePoint = contextualToolbarType => {
      return this.multiRowCollapsePoint[contextualToolbarType];
    };
    this.setCollapsePoint = (contextualToolbarType, totalInnerWidth, collapsePoint = 0) => {
      const defaultCollapsePoint = totalInnerWidth * 3 / 5;
      const curCollapsePoint = this.multiRowCollapsePoint[contextualToolbarType];
      const updatedCollapsePoint = curCollapsePoint ? Math.max(curCollapsePoint, collapsePoint) : defaultCollapsePoint;
      if (updatedCollapsePoint !== curCollapsePoint) {
        this.multiRowCollapsePoint[contextualToolbarType] = updatedCollapsePoint;
      }
    };
    this.getControlWidth = ({
      type,
      control
    }) => {
      let controlName = control;
      if (type === ToolbarControlTypes.DIVIDER) {
        return DIVIDER_WIDTH;
      } else if (type === ToolbarControlTypes.MORE_MENU) {
        controlName = 'moremenu';
      }
      return controlName ? this.controlRects[controlName].width : 0;
    };
    this.id = getToolbarId();
    const hasRows = Array.isArray(props.toolbar[0]);
    this.state = {
      toolbarWidth: 0,
      maxInnerWidths: [],
      totalInnerWidth: 0,
      controlRects: {},
      fontsLoaded: false,
      multiRow: false,
      contextualToolbar: {
        maxInnerWidths: [],
        totalInnerWidth: 0
      },
      contextualToolbarType: NORMAL_TOOLBAR,
      emptyMenuControls: [],
      hasRows,
      isMultiRowSupported: hasRows
    };
    this.controlRects = {};
    this.rows = [];
    this.multiRowCollapsePoint = {};
    this.handleResize = this.handleResize.bind(this);
    this.collectControlRect = this.collectControlRect.bind(this);
    this.getToolbarWidth = this.getToolbarWidth.bind(this);
    this.getResponsiveToolbar = memoize(this.getResponsiveToolbar.bind(this), (__toolbar, toolbarWidth, maxInnerWidths, totalInnerWidth, multiRow, contextualToolbarType) => `${toolbarWidth}:${maxInnerWidths.join('-')}:${totalInnerWidth}:${multiRow}:${contextualToolbarType}`);
    this.controlsCollected = debounce(this.controlsCollected.bind(this), 100);
    this.renderControlGroup = this.renderControlGroup.bind(this);
  }
  static getDerivedStateFromProps(nextProps, prevState) {
    const {
      contextualToolbarType,
      toolbar
    } = nextProps;
    if (contextualToolbarType !== prevState.contextualToolbarType) {
      const hasRows = Array.isArray(toolbar[0]);
      return {
        hasRows,
        multiRow: false,
        maxInnerWidths: [],
        totalInnerWidth: 0,
        contextualToolbar: {
          maxInnerWidths: [],
          totalInnerWidth: 0
        },
        contextualToolbarType: contextualToolbarType || NORMAL_TOOLBAR,
        emptyMenuControls: getEmptyMenuControls(hasRows ? toolbar : [toolbar])
      };
    }
    return null;
  }
  componentDidMount() {
    this.setState({
      toolbarWidth: this.getToolbarWidth()
    });
    this.initResponsiveToolbar();
  }
  componentDidUpdate(prevProps) {
    const {
      contextualToolbarType
    } = this.props;
    if (contextualToolbarType !== prevProps.contextualToolbarType) {
      window.cancelAnimationFrame(this.animationFrameId);
      this.handleResize();
    }
  }
  componentWillUnmount() {
    window.cancelAnimationFrame(this.animationFrameId);
    this.controlsCollected.reset();
  }
  initResponsiveToolbar() {
    iconFontsLoadedPromise.then(() => {
      this.setState({
        fontsLoaded: true
      }, this.handleResize);
    });
  }
  getClonedToolbar(toolbar) {
    const mapGroup = group => {
      return Object.assign({}, group, {
        controls: group.controls.map(control => Object.assign({}, control))
      });
    };
    if (this.state.hasRows) {
      return toolbar.map(row => row.map(mapGroup));
    }
    return toolbar.map(mapGroup);
  }
  getToolbarWidth() {
    if (!this.outerToolbar) {
      return 0;
    }
    return this.outerToolbar.offsetWidth - OUTER_TOOLBAR_MARGINS;
  }
  getInnerToolbarWidth() {
    return this.getTotalInnerWidth(this.getInnerWidths());
  }
  getResponsiveGroupIndex(toolbar, multiRow, rowIdx, collapsedGroups) {
    let responsiveRowIndex = 0;
    let responsiveGroupIndex = -1;
    let priority = Infinity;
    toolbar.forEach((row, rowIndex) => {
      if (multiRow && rowIndex !== rowIdx) return;
      row.forEach((group, groupIndex) => {
        const {
          responsivePriority = Infinity
        } = group;

        // Only fold into menu if more than 1 item would go inside
        const shouldBeResponsive = group.boundary < group.controls.length - 2;
        if (responsivePriority < priority && !collapsedGroups[`${rowIndex}_${groupIndex}`] && shouldBeResponsive) {
          priority = responsivePriority;
          responsiveRowIndex = rowIndex;
          responsiveGroupIndex = groupIndex;
        }
      });
    });
    return {
      responsiveRowIndex,
      responsiveGroupIndex
    };
  }
  getCollapsedGroup(widthDelta, group) {
    const {
      controls,
      boundary
    } = group;
    const newControls = controls.map(control => Object.assign({}, control));
    const menuControl = newControls.pop();
    const newMenuItems = [];
    let removedControlWidths = 0;
    let threshold = false;
    if (menuControl.menuItems.length === 0) {
      // If the collapse menu is naturally empty we need to account for the
      // additional width since it will now be visible
      removedControlWidths -= this.getControlWidth(menuControl);
    }
    while (!threshold) {
      // Find next priority control to remove, defaulting to the rightmost control
      const {
        indices: indicesToRemove
      } = newControls.reduce((acc, {
        priority
      }, idx) => {
        if (priority > acc.priority) {
          acc.priority = priority;
          acc.indices = [idx];
        } else if (priority === acc.priority) {
          acc.indices.push(idx);
        }
        return acc;
      }, {
        priority: 0,
        indices: [newControls.length - 1]
      });
      indicesToRemove.sort((a, b) => a - b);
      while (indicesToRemove.length) {
        // Remove the control
        const indexToRemove = indicesToRemove.pop();
        const removedControl = newControls.splice(indexToRemove, 1)[0];
        const originalIndex = controls.indexOf(removedControl);

        // Add the removed control to menu items and maintain the original order
        let idx = 0;
        let inserted = false;
        while (!inserted) {
          if (idx === newMenuItems.length) {
            newMenuItems.push({
              control: removedControl.control,
              originalIndex
            });
            inserted = true;
          } else {
            if (originalIndex > newMenuItems[idx].originalIndex) {
              idx++;
            } else {
              newMenuItems.splice(idx, 0, {
                control: removedControl.control,
                originalIndex
              });
              inserted = true;
            }
          }
        }

        // Keep track of removed control widths
        const {
          width,
          marginLeft
        } = this.controlRects[removedControl.control];
        removedControlWidths += width + marginLeft;
      }
      if (removedControlWidths >= widthDelta || newControls.length <= boundary) {
        threshold = true;
      }
    }
    menuControl.menuItems = [...newMenuItems.map(({
      control
    }) => control), ...menuControl.menuItems];
    return {
      controls: [...newControls, menuControl],
      removedControlWidths
    };
  }
  getResponsiveToolbar(toolbar, toolbarWidth, maxInnerWidths, totalInnerWidth, multiRow) {
    const {
      contextualToolbarType
    } = this.state;
    const collapsedGroups = {};
    const totalWidthDelta = totalInnerWidth - toolbarWidth;
    if (totalWidthDelta < 0) {
      return [toolbar, multiRow];
    }
    const responsiveToolbar = this.getClonedToolbar(toolbar);
    const iterableToolbar = this.state.hasRows ? responsiveToolbar : [responsiveToolbar];
    let widthDeltas = [totalWidthDelta];
    const collapsedRows = [false];
    if (multiRow) {
      widthDeltas = maxInnerWidths.map((width, idx) => width - toolbarWidth - (idx === 0 ? DIVIDER_WIDTH : 0));
      collapsedRows.push(false);
    }

    // Collapse control groups for each row until the total removed widths
    // exceeds the overflow width delta, or until they're fully collapsed
    widthDeltas.forEach((widthDelta, rowIdx) => {
      let totalRemovedControlWidths = 0;
      while (totalRemovedControlWidths < widthDelta && !collapsedRows[rowIdx]) {
        const {
          responsiveRowIndex,
          responsiveGroupIndex
        } = this.getResponsiveGroupIndex(iterableToolbar, multiRow, rowIdx, collapsedGroups);
        if (multiRow && responsiveRowIndex !== rowIdx) {
          collapsedRows[rowIdx] = true;
        } else if (responsiveGroupIndex >= 0) {
          const {
            controls,
            removedControlWidths
          } = this.getCollapsedGroup(widthDelta - totalRemovedControlWidths, iterableToolbar[responsiveRowIndex][responsiveGroupIndex]);
          collapsedGroups[`${responsiveRowIndex}_${responsiveGroupIndex}`] = controls;
          totalRemovedControlWidths += removedControlWidths;
        } else {
          collapsedRows[rowIdx] = true;
        }
      }
    });

    // Move all `multiRowResponsive` controls out of the second row to the first
    if (contextualToolbarType && multiRow && collapsedRows[1]) {
      const topRow = iterableToolbar[0];
      const {
        bottomRow,
        removedControlGroups
      } = iterableToolbar[1].reduce((acc, control) => {
        if (control.multiRowResponsive) {
          acc.removedControlGroups.push(control);
        } else {
          acc.bottomRow.push(control);
        }
        return acc;
      }, {
        bottomRow: [],
        removedControlGroups: []
      });
      if (removedControlGroups.length) {
        topRow.push(...removedControlGroups);
        const controlGroupWidths = removedControlGroups.reduce((removedWidths, controlGroup) => {
          const {
            controls
          } = controlGroup;
          const controlWidths = controls.reduce((widths, control) => widths + this.getControlWidth(control), 0);
          return removedWidths + controlWidths;
        }, 0);
        const firstRowInnerWidth = maxInnerWidths[0] + controlGroupWidths;
        const secondRowInnerWidth = maxInnerWidths[1] - controlGroupWidths;
        return this.getResponsiveToolbar([topRow, bottomRow], toolbarWidth, [firstRowInnerWidth, secondRowInnerWidth], totalInnerWidth, multiRow);
      }
    }
    const fullyCollapsed = collapsedRows.reduce((acc, isCollapsed) => acc && isCollapsed, this.state.hasRows);
    const innerToolbarWidth = this.getInnerToolbarWidth();
    const shouldCollapse = toolbarWidth < this.getCollapsePoint(contextualToolbarType);
    const hitCollapsePoint = fullyCollapsed && toolbarWidth < innerToolbarWidth;

    // Split the toolbar into multiple rows if we've reached a collapse point
    if (this.state.isMultiRowSupported && !multiRow && (shouldCollapse || hitCollapsePoint)) {
      if (hitCollapsePoint) {
        this.setCollapsePoint(contextualToolbarType, totalInnerWidth, innerToolbarWidth);
      }
      this.setState({
        multiRow: true
      });
    }

    // Replace the control groups with their collapsed versions
    Object.keys(collapsedGroups).forEach(collapsedGroupKey => {
      const [rowIndex, groupIndex] = collapsedGroupKey.split('_');
      iterableToolbar[rowIndex][groupIndex] = Object.assign({}, iterableToolbar[rowIndex][groupIndex], {
        controls: collapsedGroups[collapsedGroupKey]
      });
    });
    return [responsiveToolbar, multiRow];
  }
  handleResize() {
    const {
      contextualToolbarType
    } = this.props;
    const toolbarWidth = this.getToolbarWidth();
    let maxInnerWidths = this.state.maxInnerWidths;
    let totalInnerWidth = this.state.totalInnerWidth;
    if (contextualToolbarType) {
      maxInnerWidths = this.state.contextualToolbar.maxInnerWidths;
      totalInnerWidth = this.state.contextualToolbar.totalInnerWidth;
    }
    if (this.innerToolbar && !totalInnerWidth) {
      // Subtract each empty menu item width from the total
      const emptyMenuWidths = this.state.emptyMenuControls.map(row => row.reduce((widths, control) => {
        return widths - this.getControlWidth(control);
      }, 0));
      maxInnerWidths = this.getInnerWidths(emptyMenuWidths);
      totalInnerWidth = this.getTotalInnerWidth(maxInnerWidths);
      this.setCollapsePoint(contextualToolbarType, totalInnerWidth);
    }
    const updatedState = {};
    let shouldUpdateState = false;
    if (contextualToolbarType) {
      if (this.state.toolbarWidth && !this.state.contextualToolbar.totalInnerWidth) {
        updatedState.contextualToolbar = {
          maxInnerWidths,
          totalInnerWidth
        };
        shouldUpdateState = true;
      }
    } else {
      if (this.state.toolbarWidth && !this.state.totalInnerWidth) {
        updatedState.maxInnerWidths = maxInnerWidths;
        updatedState.totalInnerWidth = totalInnerWidth;
        shouldUpdateState = true;
      }
    }
    if (this.state.toolbarWidth !== toolbarWidth) {
      updatedState.toolbarWidth = toolbarWidth;
      shouldUpdateState = true;
    }
    if (this.state.isMultiRowSupported && this.state.hasRows && toolbarWidth && totalInnerWidth) {
      // Render a multi-row toolbar if the collapse point has been reached
      const shouldBeMultiRow = toolbarWidth < this.getCollapsePoint(contextualToolbarType);
      if (this.state.multiRow !== shouldBeMultiRow) {
        updatedState.multiRow = shouldBeMultiRow;
        shouldUpdateState = true;
      }
    }
    if (shouldUpdateState) {
      this.setState(updatedState);
    }
    this.animationFrameId = window.requestAnimationFrame(this.handleResize);
  }
  controlsCollected() {
    if (Object.keys(this.state.controlRects).length !== Object.keys(this.controlRects).length) {
      this.setState({
        controlRects: this.controlRects
      });
    }
  }
  collectControlRect(control, boundingRect) {
    this.controlRects[control] = boundingRect;
    this.controlsCollected();
  }
  renderControlGroup({
    controls
  }, index) {
    const {
      centerControls,
      dropdownClassName,
      convertToButton,
      convertToMenuItem,
      onMoreMenuOpenComplete
    } = this.props;
    const additionalProps = {};
    if (this.props.showToolbarShepherd) {
      additionalProps.toolbarShepherdProps = {
        className: this.props.shepherdClassName,
        getUserAttributes: this.props.getUserAttributes,
        setUserAttribute: this.props.setUserAttribute
      };
    }
    return /*#__PURE__*/_jsx(ControlGroup, Object.assign({
      controls: controls,
      dropdownClassName: dropdownClassName,
      centerControls: centerControls,
      convertToButton: convertToButton,
      convertToMenuItem: convertToMenuItem,
      collectControlRect: this.collectControlRect,
      onMoreMenuOpenComplete: onMoreMenuOpenComplete,
      toolbarWidth: this.state.toolbarWidth,
      controlRects: this.state.controlRects,
      contextualNodeAttributes: this.props.contextualNodeAttributes
    }, additionalProps), `toolbar-${this.id}-control-group-${index}`);
  }
  renderToolbar(toolbar, multiRow) {
    if (this.state.hasRows) {
      return toolbar.map((row, index) => {
        return /*#__PURE__*/_jsx(Fragment, {
          children: /*#__PURE__*/_jsxs(StyledRow, {
            ref: el => this.rows[index] = el,
            children: [row.map(this.renderControlGroup), index === 0 && /*#__PURE__*/_jsx(DividerContainer, {
              children: /*#__PURE__*/_jsx(Divider, {
                multiRow: multiRow
              }, "row-divider")
            })]
          }, `toolbar-${this.id}-row-${index}`)
        }, index);
      });
    }
    return /*#__PURE__*/_jsx(StyledRow, {
      ref: el => this.rows[0] = el,
      children: toolbar.map(this.renderControlGroup)
    }, `toolbar-${this.id}-row-${0}`);
  }
  render() {
    const {
      forwardRef,
      centerControls,
      uploadProgress,
      renderFindReplace
    } = this.props;
    const {
      toolbarWidth,
      contextualToolbarType,
      isMultiRowSupported,
      hasRows
    } = this.state;
    let toolbar;
    let multiRow = false;
    let innerToolbarProps = {};
    if (toolbarWidth) {
      let {
        maxInnerWidths,
        totalInnerWidth
      } = this.state;
      if (contextualToolbarType) {
        maxInnerWidths = this.state.contextualToolbar.maxInnerWidths;
        totalInnerWidth = this.state.contextualToolbar.totalInnerWidth;
        const singleRowToolbar = !hasRows;
        const multiRowToolbarNotSplit = hasRows && totalInnerWidth && !this.state.multiRow;
        innerToolbarProps = {
          'data-test-id': 'canvas-contextual-toolbar',
          expandedHeight: isMultiRowSupported && toolbarWidth <= this.getCollapsePoint(NORMAL_TOOLBAR) && (singleRowToolbar || multiRowToolbarNotSplit)
        };
      }
      const responsiveToolbar = this.getResponsiveToolbar(this.props.toolbar, toolbarWidth, maxInnerWidths, totalInnerWidth, this.state.multiRow, contextualToolbarType);
      toolbar = responsiveToolbar[0];
      multiRow = responsiveToolbar[1];
    }
    return /*#__PURE__*/_jsx(Disableable, {
      disabled: this.props.disabled,
      toolbarTooltip: this.props.toolbarTooltip,
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsxs(StyledToolbar, {
          "data-test-id": "canvas-toolbar",
          ref: el => {
            forwardRef(el);
            this.outerToolbar = el;
          },
          className: this.props.className,
          children: [/*#__PURE__*/_jsx(StyledInnerToolbar, Object.assign({}, innerToolbarProps, {
            multiRow: multiRow,
            centerControls: centerControls,
            ref: el => this.innerToolbar = el,
            children: toolbar !== undefined && this.renderToolbar(toolbar, multiRow)
          }), "styled-inner-toolbar"), uploadProgress && /*#__PURE__*/_jsx(UIAbstractProgress, {
            Component: UINanoProgress,
            value: uploadProgress
          })]
        }, `toolbar-${this.id}`), typeof renderFindReplace === 'function' && renderFindReplace()]
      })
    });
  }
}
const toolbarRowPropType = PropTypes.arrayOf(PropTypes.shape({
  controls: PropTypes.arrayOf(controlPropType).isRequired,
  boundary: PropTypes.number,
  responsivePriority: PropTypes.number
}));
Toolbar.propTypes = {
  forwardRef: PropTypes.func,
  toolbar: PropTypes.oneOfType([toolbarRowPropType, PropTypes.arrayOf(toolbarRowPropType)]).isRequired,
  contextualNodeAttributes: PropTypes.object,
  contextualToolbarType: PropTypes.string,
  convertToMenuItem: PropTypes.func.isRequired,
  convertToButton: PropTypes.func.isRequired,
  onMoreMenuOpenComplete: PropTypes.func,
  dropdownClassName: PropTypes.string,
  className: PropTypes.string,
  centerControls: PropTypes.bool,
  shepherdClassName: PropTypes.string,
  getUserAttributes: PropTypes.func,
  setUserAttribute: PropTypes.func,
  showToolbarShepherd: PropTypes.bool,
  disabled: PropTypes.bool,
  toolbarTooltip: PropTypes.string,
  uploadProgress: PropTypes.number,
  renderFindReplace: PropTypes.func
};
Toolbar.defaultProps = {
  centerControls: false,
  convertToButton: identity,
  convertToMenuItem: identity,
  showToolbarShepherd: false,
  disabled: false,
  forwardRef: () => {},
  toolbar: [],
  contextualNodeAttributes: {},
  contextualToolbarType: NORMAL_TOOLBAR
};